import { platformStyle } from './platforms';
import type { SocialPost } from './queries';

/**
 * Social Studio filtering.
 *
 * Kept as pure functions over an already-loaded array rather than as query
 * parameters. Every view on the page reads the same 105-row list, so filtering
 * in memory means switching Board to Calendar keeps the filter without a
 * refetch, and the counts in the tab badges agree with what the view shows.
 *
 * Campaigns are deliberately absent from this shape: there is no campaign
 * column on social_posts, and a dropdown that cannot filter anything is worse
 * than no dropdown.
 */

export interface SocialFilters {
  search: string;
  platform: string;
  status: string;
  pillar: string;
  assignee: string;
}

export const EMPTY_SOCIAL_FILTERS: SocialFilters = {
  search: '',
  platform: '',
  status: '',
  pillar: '',
  assignee: '',
};

/** Sentinel for "no assignee set", which is a real answer rather than "any". */
export const UNASSIGNED = '__unassigned__';

export function isFiltered(filters: SocialFilters): boolean {
  return (
    filters.search.trim() !== '' ||
    filters.platform !== '' ||
    filters.status !== '' ||
    filters.pillar !== '' ||
    filters.assignee !== ''
  );
}

/** How many dropdowns are narrowing the list, ignoring the search box. */
export function activeFilterCount(filters: SocialFilters): number {
  return [filters.platform, filters.status, filters.pillar, filters.assignee].filter(
    (value) => value !== '',
  ).length;
}

/**
 * Platforms are typed freehand, so the options come from the data rather than
 * from a fixed list. Normalising through platformStyle means "Tik Tok" and
 * "tiktok" collapse to one option instead of two near-identical ones.
 */
export function platformOptions(posts: SocialPost[]): { value: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const post of posts) {
    for (const raw of post.platforms) {
      if (raw.trim() === '') continue;
      const style = platformStyle(raw);
      if (!seen.has(style.key)) {
        seen.set(style.key, style.label);
      }
    }
  }
  return [...seen.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Pillars are freehand too, and empty is common, so blanks never become options. */
export function pillarOptions(posts: SocialPost[]): string[] {
  const seen = new Set<string>();
  for (const post of posts) {
    const pillar = post.pillar.trim();
    if (pillar !== '') {
      seen.add(pillar);
    }
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

function matchesSearch(post: SocialPost, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (needle === '') return true;
  return [post.title, post.caption, post.notes ?? '', post.pillar].some((field) =>
    field.toLowerCase().includes(needle),
  );
}

function matchesPlatform(post: SocialPost, platform: string): boolean {
  if (platform === '') return true;
  return post.platforms.some((raw) => platformStyle(raw).key === platform);
}

function matchesAssignee(post: SocialPost, assignee: string): boolean {
  if (assignee === '') return true;
  if (assignee === UNASSIGNED) return post.assignee_id === null;
  return post.assignee_id === assignee;
}

export function filterSocialPosts(posts: SocialPost[], filters: SocialFilters): SocialPost[] {
  return posts.filter(
    (post) =>
      matchesSearch(post, filters.search) &&
      matchesPlatform(post, filters.platform) &&
      (filters.status === '' || post.status === filters.status) &&
      (filters.pillar === '' || post.pillar.trim() === filters.pillar) &&
      matchesAssignee(post, filters.assignee),
  );
}

/** Posts awaiting a decision, for the For Approval tab badge. */
export function forApprovalCount(posts: SocialPost[]): number {
  return posts.filter((post) => post.status === 'for_approval').length;
}
